import { useState, useMemo, useEffect, useRef } from 'react'
import { useAuthStore } from '@/stores/auth-store'
import { RefreshCw, Moon, Sun, AlertCircle, Server, Shield, Scale, Lock } from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { InfrastructureSummary, PdpStatusCard } from '@/components/infrastructure'
import { useInfrastructureStatus } from '@/hooks/use-infrastructure'
import { useThemeStore } from '@/stores/theme-store'
import { cn } from '@/lib/utils'
import type { InfrastructureStatus as InfraStatus, PdpInstance, PepInstance, InfrastructureSummary as InfraSummary } from '@/types/infrastructure'

function StatusDot({ healthy }: { readonly healthy: boolean }) {
  return (
    <span
      className={cn(
        'inline-block h-2.5 w-2.5 rounded-full',
        healthy ? 'bg-green-500' : 'bg-red-500'
      )}
    />
  )
}

type NodeVariant = 'pap' | 'pdp' | 'pep'

const VARIANT_STYLES: Record<NodeVariant, {
  readonly border: string
  readonly bg: string
  readonly icon: typeof Shield
  readonly iconColor: string
}> = {
  pap: {
    border: 'border-l-4 border-l-blue-500',
    bg: 'bg-blue-50/50 dark:bg-blue-950/20',
    icon: Shield,
    iconColor: 'text-blue-500',
  },
  pdp: {
    border: 'border-l-4 border-l-amber-500',
    bg: 'bg-amber-50/50 dark:bg-amber-950/20',
    icon: Scale,
    iconColor: 'text-amber-500',
  },
  pep: {
    border: 'border-l-4 border-l-emerald-500',
    bg: 'bg-emerald-50/50 dark:bg-emerald-950/20',
    icon: Lock,
    iconColor: 'text-emerald-500',
  },
}

const COLUMN_HEADERS: Record<NodeVariant, { readonly label: string; readonly color: string }> = {
  pap: { label: 'Administration', color: 'text-blue-600 dark:text-blue-400' },
  pdp: { label: 'Decision', color: 'text-amber-600 dark:text-amber-400' },
  pep: { label: 'Enforcement', color: 'text-emerald-600 dark:text-emerald-400' },
}

function TopologyNodeCard({
  label,
  type,
  healthy,
  variant,
  version,
  lastSeen,
  linkUrl,
}: {
  readonly label: string
  readonly type: string
  readonly healthy: boolean
  readonly variant: NodeVariant
  readonly version?: string
  readonly lastSeen?: string
  /**
   * Deprecated. The PEP's admin UI is reachable from the Enforcement
   * Admin tab on this same page (iframe embed) — a separate
   * new-tab link broke the flow (operators left the PAP shell and
   * lost the iframe handshake). Kept in the props type for back-compat
   * with callers that still pass it; intentionally not rendered.
   */
  readonly linkUrl?: string
}) {
  const styles = VARIANT_STYLES[variant]
  const Icon = styles.icon
  // linkUrl intentionally ignored — see prop comment above.
  void linkUrl

  const titleEl = (
    <CardTitle className="text-sm font-medium truncate">{label}</CardTitle>
  )

  return (
    <Card className={cn('w-56 shrink-0', styles.border, styles.bg)}>
      <CardHeader className="p-3 pb-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            <Icon className={cn('h-4 w-4 shrink-0', styles.iconColor)} />
            {titleEl}
          </div>
          <StatusDot healthy={healthy} />
        </div>
        <p className="text-xs text-muted-foreground">{type}</p>
      </CardHeader>
      <CardContent className="p-3 pt-0 space-y-0.5 text-xs text-muted-foreground">
        {version && <p>v{version}</p>}
        {lastSeen && (
          <p title={lastSeen}>
            {new Date(lastSeen).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function ColumnHeader({ variant }: { readonly variant: NodeVariant }) {
  const header = COLUMN_HEADERS[variant]
  const styles = VARIANT_STYLES[variant]
  const Icon = styles.icon

  return (
    <div className="flex items-center gap-1.5 mb-4">
      <Icon className={cn('h-4 w-4', styles.iconColor)} />
      <span className={cn('text-xs font-semibold uppercase tracking-wider', header.color)}>
        {header.label}
      </span>
    </div>
  )
}

function ConnectorLine() {
  return (
    <div className="flex items-center px-3 pt-8">
      <div className="w-10 border-t-2 border-dashed border-muted-foreground/40" />
      <div className="h-0 w-0 border-y-[5px] border-y-transparent border-l-[7px] border-l-muted-foreground/40" />
    </div>
  )
}

function LandscapeView({ data }: { readonly data: InfraStatus | undefined }) {
  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <Server className="h-12 w-12 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">
          No infrastructure data available yet.
        </p>
      </div>
    )
  }

  const hasPdps = data.pdps.length > 0
  const hasPeps = data.pdps.some((pdp) => pdp.peps.length > 0)

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Visual topology of the poli-x infrastructure showing PAP, PDP, and PEP connections.
      </p>

      <div className="flex items-start overflow-x-auto pb-4">
        {/* PAP Column */}
        <div className="flex flex-col">
          <ColumnHeader variant="pap" />
          <div className="flex items-start">
            <TopologyNodeCard
              label="PAP"
              type="Policy Administration Point"
              healthy={true}
              variant="pap"
              version="1.0"
            />
          </div>
        </div>

        {/* Connector: PAP -> PDP */}
        {hasPdps && <ConnectorLine />}

        {/* PDP Column */}
        <div className="flex flex-col">
          <ColumnHeader variant="pdp" />
          {!hasPdps ? (
            <div className="flex h-24 w-56 items-center justify-center rounded-lg border border-dashed">
              <p className="text-xs text-muted-foreground">No PDPs registered</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {data.pdps.map((pdp: PdpInstance) => (
                <TopologyNodeCard
                  key={pdp.pdpId}
                  label={pdp.pdpId}
                  type="Policy Decision Point"
                  healthy={pdp.status === 'connected'}
                  variant="pdp"
                  version={pdp.version}
                  lastSeen={pdp.lastHeartbeat}
                />
              ))}
            </div>
          )}
        </div>

        {/* Connector: PDP -> PEP */}
        {hasPeps && <ConnectorLine />}

        {/* PEP Column */}
        {hasPeps && (
          <div className="flex flex-col">
            <ColumnHeader variant="pep" />
            <div className="flex flex-col gap-3">
              {data.pdps.flatMap((pdp: PdpInstance) =>
                pdp.peps.map((pep: PepInstance) => {
                  const adminUrl =
                    typeof pep.metadata?.adminUrl === 'string'
                      ? (pep.metadata.adminUrl as string)
                      : undefined
                  return (
                    <TopologyNodeCard
                      key={pep.pepId}
                      label={pep.pepId}
                      type={`PEP · ${pep.pepType} · ${pdp.pdpId}`}
                      healthy={pep.status === 'active'}
                      variant="pep"
                      lastSeen={pep.lastSeen}
                      linkUrl={adminUrl}
                    />
                  )
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function InfrastructurePage() {
  const { data, isLoading, isError, error, refetch } = useInfrastructureStatus()
  const { theme, toggleTheme } = useThemeStore()
  const [showActiveOnly, setShowActiveOnly] = useState(true)

  const filteredData = useMemo((): InfraStatus | undefined => {
    if (!data) return undefined
    if (!showActiveOnly) return data

    const filteredPdps = data.pdps
      .filter((pdp) => pdp.status === 'connected')
      .map((pdp) => ({
        ...pdp,
        peps: pdp.peps.filter((pep) => pep.status === 'active'),
      }))

    const summary: InfraSummary = {
      totalPdps: filteredPdps.length,
      connectedPdps: filteredPdps.length,
      totalPeps: filteredPdps.reduce((sum, pdp) => sum + pdp.peps.length, 0),
      activePeps: filteredPdps.reduce((sum, pdp) => sum + pdp.peps.length, 0),
    }

    return { pdps: filteredPdps, summary }
  }, [data, showActiveOnly])

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-base font-medium">Infrastructure</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 mr-2">
            <Switch
              id="active-filter"
              checked={showActiveOnly}
              onCheckedChange={setShowActiveOnly}
            />
            <Label htmlFor="active-filter" className="text-sm cursor-pointer">
              Active only
            </Label>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
            className="h-8"
          >
            <RefreshCw
              className={cn('mr-2 h-3.5 w-3.5', isLoading && 'animate-spin')}
            />
            Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        {/* Error state */}
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                Failed to load infrastructure status
                {error instanceof Error ? `: ${error.message}` : ''}
              </span>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="status" className="flex-1">
          <TabsList>
            <TabsTrigger value="status">Status</TabsTrigger>
            <TabsTrigger value="landscape">Landscape</TabsTrigger>
            <TabsTrigger value="enforcement-admin">Enforcement Admin</TabsTrigger>
          </TabsList>

          <TabsContent value="status" className="mt-4 space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">
                Monitor PDP and PEP instances connected to your policy platform. Status updates every 15 seconds.
              </p>
            </div>

            {/* Summary cards */}
            <InfrastructureSummary summary={filteredData?.summary} isLoading={isLoading && !data} />

            {/* PDP instance cards */}
            {isLoading && !data ? (
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-64 w-full rounded-xl" />
                ))}
              </div>
            ) : filteredData?.pdps.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <Server className="h-12 w-12 text-muted-foreground/50" />
                <div>
                  <h3 className="text-lg font-medium">
                    {showActiveOnly ? 'No active PDP instances' : 'No PDP instances registered'}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {showActiveOnly
                      ? 'All PDPs are currently disconnected. Toggle "Active only" off to see all instances.'
                      : 'PDP instances will appear here once they connect and send their first heartbeat.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredData?.pdps.map((pdp) => (
                  <PdpStatusCard key={pdp.pdpId} pdp={pdp} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="landscape" className="mt-4">
            <LandscapeView data={filteredData} />
          </TabsContent>

          {/* Embeds polix-pep-ariba's enforcement-admin UI inline (iframe).
              Iframe src comes from VITE_ENFORCEMENT_ADMIN_URL set at build
              time. In dev builds it falls back to http://localhost:4000.
              In production builds with no env var set, the iframe shows a
              configuration-error message instead of pointing at localhost.
              Operator stays in PAP — no new tab — sidebar visible.

              Identity propagation: posts the logged-in user's identity to
              the embedded SPA so its API calls carry X-User-Id headers and
              audit events land in PAP's audit log with the correct
              actorId. The child SPA signals readiness via
              `polix-user-ready`; we also re-send on user change. */}
          <TabsContent value="enforcement-admin" className="mt-4">
            <EnforcementAdminEmbed />
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}

/**
 * Iframe embed for polix-pep-ariba's enforcement-admin SPA. Wires up
 * postMessage-based identity sync so the child SPA can attribute audit
 * events to the real PAP user.
 *
 * Two-part handshake:
 *  1. Mount the iframe pointing at VITE_ENFORCEMENT_ADMIN_URL.
 *  2. When the child sends `polix-user-ready` (its hooks/use-pap-actor.ts
 *     posts this on mount), we reply with `polix-user` carrying
 *     {userId, email, roles}. Belt-and-suspenders: we also push on every
 *     user change after ready.
 *
 * Origin handling: targetOrigin is derived from the iframe URL so messages
 * never leak to a different host. Allowed inbound origin is the same one.
 */
/**
 * Resolve the enforcement-admin URL for the iframe.
 *
 * Priority:
 *   1. VITE_ENFORCEMENT_ADMIN_URL set at build time → use it (preferred for
 *      production deploys; CI sets it to e.g. https://enforcement-admin.staging.poli-x.com).
 *   2. Dev build (`import.meta.env.DEV`) → fall back to localhost:4000 so
 *      `npm run dev` keeps working when running the PAP frontend alongside
 *      enforcement-admin locally.
 *   3. Production build with no env var → return empty string. The component
 *      renders a config-error message instead of pointing the iframe at
 *      `http://localhost:4000`, which is what users-on-staging-with-no-local-
 *      enforcement-admin saw before this fix ("localhost refused to connect").
 *
 * Returning empty + showing an error is intentional: silently leaking
 * `http://localhost:4000` into a production bundle is the bug we're fixing.
 * Better to fail loudly so an operator notices and either sets the env var
 * or investigates.
 */
function resolveIframeUrl(): string {
  const fromEnv = import.meta.env.VITE_ENFORCEMENT_ADMIN_URL as string | undefined
  if (fromEnv) return fromEnv
  if (import.meta.env.DEV) return 'http://localhost:4000'
  return ''
}

function EnforcementAdminEmbed() {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const user = useAuthStore((s) => s.user)
  // Forward the PAP-issued access token into the embedded admin so its
  // /api/* auth middleware (services/enforcement-admin/src/middleware/auth.ts)
  // can validate every backend call. Same JWT_SECRET signs both sides;
  // the embedded admin verifies HS256 + ties each call to the operator.
  const accessToken = useAuthStore((s) => s.tokens?.accessToken)
  const theme = useThemeStore((s) => s.theme)
  const [userReady, setUserReady] = useState(false)
  const [themeReady, setThemeReady] = useState(false)

  const iframeUrl = resolveIframeUrl()
  const iframeOrigin = useMemo(() => {
    if (!iframeUrl) return ''
    try {
      return new URL(iframeUrl).origin
    } catch {
      return ''
    }
  }, [iframeUrl])

  // Resolve 'system' to its concrete value at the moment we send.
  // The child only knows 'dark' or 'light'.
  const effectiveTheme = useMemo<'dark' | 'light'>(() => {
    if (theme === 'dark' || theme === 'light') return theme
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }, [theme])

  // Stable refs so the message handler can read current values without
  // re-binding (which would lose the listener on every render). The
  // iframe can navigate (e.g. operator clicks "Learn more" → /docs/*)
  // and reloads its SPA on each navigation; each fresh load posts a
  // `polix-user-ready` ping — we MUST respond to every ping with a
  // fresh `polix-user` push, not just the first one. Earlier code used
  // a `userReady` state flag which only flipped once, so subsequent
  // navigations never received the token. Now we push directly from
  // the handler.
  const userRef = useRef(user)
  const accessTokenRef = useRef(accessToken)
  useEffect(() => { userRef.current = user }, [user])
  useEffect(() => { accessTokenRef.current = accessToken }, [accessToken])

  useEffect(() => {
    if (!iframeOrigin) return
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== iframeOrigin) return
      const data = e.data as { type?: string }
      if (data?.type === 'polix-user-ready') {
        setUserReady(true)
        // Respond immediately with the latest user + token. Reading
        // from refs (not closure) so an iframe reload after a token
        // refresh still receives the rotated token.
        const u = userRef.current
        if (u) {
          const win = iframeRef.current?.contentWindow
          win?.postMessage(
            {
              type: 'polix-user',
              userId: u.id,
              email: u.email,
              roles: u.roles.join(','),
              token: accessTokenRef.current,
            },
            iframeOrigin,
          )
        }
      }
      if (data?.type === 'polix-theme-ready') setThemeReady(true)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [iframeOrigin])

  // Re-push when user or token changes mid-session (e.g. token refresh
  // while the iframe is already loaded — no fresh ready-ping fires in
  // that case, so we push proactively here).
  useEffect(() => {
    if (!userReady || !user || !iframeOrigin) return
    const win = iframeRef.current?.contentWindow
    if (!win) return
    win.postMessage(
      {
        type: 'polix-user',
        userId: user.id,
        email: user.email,
        roles: user.roles.join(','),
        token: accessToken,
      },
      iframeOrigin,
    )
  }, [userReady, user, accessToken, iframeOrigin])

  // Push theme once the child is ready (and re-push on every theme change in PAP)
  useEffect(() => {
    if (!themeReady || !iframeOrigin) return
    const win = iframeRef.current?.contentWindow
    if (!win) return
    win.postMessage({ type: 'polix-theme', theme: effectiveTheme }, iframeOrigin)
  }, [themeReady, effectiveTheme, iframeOrigin])

  // Production build with no env var → iframeUrl is empty. Render a
  // configuration-error message instead of pointing the iframe at a
  // wrong default.
  if (!iframeUrl) {
    return (
      <div className="flex h-[calc(100vh-220px)] w-full flex-col items-center justify-center gap-4 rounded-lg border bg-muted/30 p-8 text-center">
        <h3 className="text-lg font-semibold">Enforcement Admin not configured</h3>
        <p className="max-w-md text-sm text-muted-foreground">
          This deployment was built without <code className="rounded bg-muted px-1 py-0.5 text-xs">VITE_ENFORCEMENT_ADMIN_URL</code>.
          Set it at build time in your CI/CD pipeline to the URL of your
          enforcement-admin instance (e.g. <code className="rounded bg-muted px-1 py-0.5 text-xs">https://enforcement-admin.staging.poli-x.com</code>),
          then rebuild and redeploy the PAP frontend.
        </p>
        <p className="max-w-md text-xs text-muted-foreground">
          Operators: until then, open the enforcement admin in a separate browser tab using whatever URL it's hosted at.
        </p>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-220px)] w-full overflow-hidden rounded-lg border">
      <iframe
        ref={iframeRef}
        src={iframeUrl}
        title="Polix Enforcement Admin"
        className="h-full w-full border-0"
      />
    </div>
  )
}

export default InfrastructurePage
